import type {
  ProductTourCardPlacement,
  ProductTourPresentation,
} from './types';

export type TourAnchorRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

export type TourCardPosition = {
  top: number;
  left: number;
  placement: 'center' | 'beside' | 'overlay' | 'above';
};

const CARD_WIDTH = 340;
const CARD_GAP = 16;
const CARD_ESTIMATED_HEIGHT = 320;

export const PRODUCT_TOUR_CARD_WIDTH = CARD_WIDTH;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

export const findVisibleTourAnchor = (
  anchorId: string | null | undefined,
): Element | null => {
  if (!anchorId || typeof document === 'undefined') {
    return null;
  }
  const matches = Array.from(
    document.querySelectorAll(`[data-tour-id="${anchorId}"]`),
  );
  const visible = matches.find((element) => {
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  });
  return visible ?? matches[0] ?? null;
};

export const readTourAnchorRect = (
  anchorId: string | null,
): TourAnchorRect | null => {
  const element = findVisibleTourAnchor(anchorId);
  if (!element) {
    return null;
  }
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) {
    return null;
  }
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  };
};

const resolvePlacement = ({
  presentation,
  cardPlacement,
  anchorRect,
  height,
}: {
  presentation: ProductTourPresentation;
  cardPlacement?: ProductTourCardPlacement;
  anchorRect: TourAnchorRect;
  height: number;
}): Exclude<ProductTourCardPlacement, 'auto'> => {
  if (cardPlacement && cardPlacement !== 'auto') {
    return cardPlacement;
  }
  if (presentation === 'coachmark') {
    return 'overlay';
  }
  if (anchorRect.top > height * 0.62) {
    return 'above';
  }
  return 'beside';
};

export const getTourCardPosition = ({
  presentation,
  anchorRect,
  viewport,
  cardPlacement = 'auto',
}: {
  presentation: ProductTourPresentation;
  anchorRect: TourAnchorRect | null;
  viewport: { width: number; height: number };
  cardPlacement?: ProductTourCardPlacement;
}): TourCardPosition => {
  const width =
    viewport.width ||
    (typeof window !== 'undefined' ? window.innerWidth : 1200);
  const height =
    viewport.height ||
    (typeof window !== 'undefined' ? window.innerHeight : 800);

  if (presentation === 'center' || !anchorRect) {
    return {
      top: Math.max(height / 2 - CARD_ESTIMATED_HEIGHT / 2, 24),
      left: Math.max(width / 2 - CARD_WIDTH / 2, 16),
      placement: 'center',
    };
  }

  const placement = resolvePlacement({
    presentation,
    cardPlacement,
    anchorRect,
    height,
  });

  if (placement === 'overlay') {
    return {
      top: clamp(height - CARD_ESTIMATED_HEIGHT - 28, 16, height - 16),
      left: clamp(width - CARD_WIDTH - 28, 16, width - CARD_WIDTH - 16),
      placement: 'overlay',
    };
  }

  if (placement === 'above') {
    const left = clamp(
      anchorRect.left - CARD_WIDTH - CARD_GAP,
      16,
      width - CARD_WIDTH - 16,
    );
    const top = clamp(
      anchorRect.top - CARD_ESTIMATED_HEIGHT - CARD_GAP * 2,
      16,
      Math.max(16, anchorRect.top - CARD_ESTIMATED_HEIGHT - CARD_GAP * 2),
    );

    return { top, left, placement: 'above' };
  }

  const spaceRight = width - (anchorRect.left + anchorRect.width + CARD_GAP);
  const spaceLeft = anchorRect.left - CARD_GAP;
  const placeRight = spaceRight >= CARD_WIDTH || spaceRight >= spaceLeft;

  const left = placeRight
    ? clamp(
        anchorRect.left + anchorRect.width + CARD_GAP,
        16,
        width - CARD_WIDTH - 16,
      )
    : clamp(
        anchorRect.left - CARD_WIDTH - CARD_GAP,
        16,
        width - CARD_WIDTH - 16,
      );

  const top = clamp(
    anchorRect.top + anchorRect.height / 2 - 80,
    16,
    height - CARD_ESTIMATED_HEIGHT - 16,
  );

  return { top, left, placement: 'beside' };
};
